import { Badge } from '@/components/ui/badge'
import type { RevenueStreamId } from '@/lib/reports/space-yield/types'
import { REVENUE_STREAMS } from '@/lib/reports/space-yield/types'

interface RevenueStreamTogglesProps {
  streams: RevenueStreamId[]
  onStreamsChange: (streams: RevenueStreamId[]) => void
  hasFuelData: boolean
}

const STREAM_COLORS: Record<RevenueStreamId, { active: string; inactive: string }> = {
  lease:     { active: 'bg-emerald-600 hover:bg-emerald-700', inactive: 'text-emerald-600 border-emerald-300 hover:bg-emerald-50' },
  transient: { active: 'bg-blue-600 hover:bg-blue-700',      inactive: 'text-blue-600 border-blue-300 hover:bg-blue-50' },
  fuel:      { active: 'bg-amber-600 hover:bg-amber-700',    inactive: 'text-amber-600 border-amber-300 hover:bg-amber-50' },
}

export function RevenueStreamToggles({ streams, onStreamsChange, hasFuelData }: RevenueStreamTogglesProps) {
  const streamsSet = new Set(streams)

  const visibleStreams = REVENUE_STREAMS.filter(
    (s) => s.id !== 'fuel' || hasFuelData,
  )

  const handleToggle = (id: RevenueStreamId) => {
    const isOn = streamsSet.has(id)
    if (isOn) {
      // Guard: prevent deselecting ALL streams
      if (streams.length <= 1) return
      onStreamsChange(streams.filter((s) => s !== id))
    } else {
      onStreamsChange([...streams, id])
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500 font-medium hidden lg:inline">Sources:</span>
      <div className="flex gap-1">
        {visibleStreams.map((stream) => {
          const isOn = streamsSet.has(stream.id)
          const colors = STREAM_COLORS[stream.id]
          return (
            <Badge
              key={stream.id}
              variant={isOn ? 'default' : 'outline'}
              className={`cursor-pointer ${isOn ? colors.active : colors.inactive}`}
              onClick={() => handleToggle(stream.id)}
            >
              {stream.label}
            </Badge>
          )
        })}
      </div>
    </div>
  )
}
